import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { PermitStep, PersonRole } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { HaTranslatePipe } from "@ha/util/pipes";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { Permit } from "../../../permit-create/models/permit.model";
import { WorkplaceDetails } from "../../../permit-create/models/workplace-details.model";
import { PermitCreateActions } from "../../../permit-create/states/permit-create.actions";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { PermitTeamActions } from "../../../permit-team/states/permit-team.action";
import { PermitTeamSelectors } from "../../../permit-team/states/permit-team.selectors";
import { CurrentStep } from "../../../permit/models/current-step.model";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";
import { PermitPerson } from "../../../shared/models/permit-person.model";
import { PermitUserSelectors } from "../../../shared/states/permit-user.selectors";
import { DetailsTab } from "../../models/details-tab.enum";
import { PermitSummaryDto, WorkMethodTranslation } from "../../models/permit-summary.model";
import { PermitDetailsActions } from "../../states/permit-details.action";
import { PermitDetailsSelectors } from "../../states/permit-details.selectors";
import { PermitCheckpointsModalComponent, PermitCheckpointsModalComponentData } from "../permit-checkpoints-modal/permit-checkpoints-modal.component";
import {
    PermitFlammableAtexPermitQuestionnaireComponent,
    PermitFlammableAtexPermitQuestionnaireComponentData,
} from "../permit-flammable-atex-permit-questionnaire/permit-flammable-atex-permit-questionnaire.component";
import {
    PermitGasFreeDeclarationComponent,
    PermitGasFreeDeclarationComponentData,
} from "../permit-gas-free-declaration/permit-gas-free-declaration.component";
import { PermitValidityPeriodModalComponent, PermitValidityPeriodModalComponentData } from "../permit-validity-period-modal/permit-validity-period-modal.component";

@Component({
    selector: "pmt-permit-summary",
    templateUrl: "permit-summary.component.html",
    styleUrls: ["permit-summary.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [HaTranslatePipe],
})
export class PermitSummaryComponent implements OnInit {
    @Input()
    public permitId: number;

    @Input()
    public forModal = false;

    @Output()
    public tabChange = new EventEmitter<DetailsTab>();

    @Select(PermitDetailsSelectors.currentUserRoles)
    public currentUserRoles$: Observable<PersonRole[]>;

    @Select(PermitDetailsSelectors.summary)
    public summary$: Observable<PermitSummaryDto>;

    @Select(PermitCreateSelectors.workplaceDetails)
    public workplaceDetails$: Observable<WorkplaceDetails>;

    @Select(PermitCreateSelectors.permit)
    public permit$: Observable<Permit>;

    @Select(PermitTeamSelectors.permitIssuer)
    public permitIssuer$: Observable<PermitPerson>;

    @Select(PermitStepSelectors.currentStep)
    public currentStep$: Observable<CurrentStep>;

    public PersonRole = PersonRole;

    public PermitStep = PermitStep;

    public workplace: string;

    public get pdfUrl() {
        return `/api/Permit/GeneratePdf?permitId=${this.permitId}`;
    }

    constructor(private store: Store, private haModalService: HaModalService, private translatePipe: HaTranslatePipe) {}

    public ngOnInit(): void {
        this.store.dispatch(new PermitCreateActions.GetActiveFixedTemplate());
        this.store.dispatch(new PermitCreateActions.GetPermit(this.permitId));
        this.store.dispatch(new PermitDetailsActions.GetSummary(this.permitId));
        this.store.dispatch(new PermitTeamActions.GetTeam(this.permitId));
        const permitsToSign = this.store.selectSnapshot(PermitUserSelectors.permitsToSign);
        const permitsToClaim = this.store.selectSnapshot(PermitUserSelectors.permitsToClaim);
        this.workplace = permitsToSign?.find((_) => _.permitId === this.permitId)?.workplace
            ?? permitsToClaim?.find((_) => _.permitId === this.permitId)?.workplace;
    }

    public canExtend(userRoles: PersonRole[]): boolean {
        return userRoles?.some((_) => _ === PersonRole.PermitIssuer);
    }

    public canComplete(userRoles: PersonRole[]): boolean {
        return userRoles?.some((_) =>
            _ === PersonRole.PermitIssuer
            || _ === PersonRole.PostFireWatcher
        );
    }

    public hasFlammableAtexContext(permit: Permit | null | undefined): boolean {
        return !!permit && permit.flammableAtexPermitId != null;
    }

    public showGasFreeDeclaration(permit: Permit | null | undefined): boolean {
        return this.hasFlammableAtexContext(permit) && permit.flammableAtexHasMeasurements;
    }

    public openPermitCheckpointsModal(): void {
        this.haModalService.open(
            PermitCheckpointsModalComponent,
            true,
            <PermitCheckpointsModalComponentData> { permitId: this.permitId },
        );
    }

    public openFlammableAtexPermitModal(): void {
        this.haModalService.open(
            PermitFlammableAtexPermitQuestionnaireComponent,
            true,
            <PermitFlammableAtexPermitQuestionnaireComponentData> { permitId: this.permitId },
        );
    }

    public openGasFreeDeclarationModal(): void {
        this.haModalService.open(
            PermitGasFreeDeclarationComponent,
            true,
            <PermitGasFreeDeclarationComponentData> { permitId: this.permitId },
        );
    }

    public getTranslatedWorkMethodText(workMethods: WorkMethodTranslation[], workMethodManualInput: string): string {
        const strArr = workMethods.map((_) => this.translatePipe.transform(_.translations));

        if (workMethodManualInput) {
            strArr.push(workMethodManualInput);
        }
        return strArr.join(", ");
    }

    public setPermitEnd(): void {
        this.tabChange.emit(DetailsTab.complete);
    }

    public openValidityPeriodModal(): void {
        this.haModalService.open(
            PermitValidityPeriodModalComponent,
            true,
            <PermitValidityPeriodModalComponentData> { permitId: this.permitId },
        );
    }
}
